import type {
  CreateContextDocumentRequest,
  ProjectContextDocument,
  UpdateContextDocumentRequest,
} from '@protoss/shared';
import * as React from 'react';
import { contextDocsApi } from '../services/contextDocsApi';

export type ContextDocsStatus = 'idle' | 'loading' | 'error';

export interface UseContextDocsReturn {
  documents: ProjectContextDocument[];
  status: ContextDocsStatus;
  error: string | null;
  refresh: () => Promise<void>;
  create: (data: CreateContextDocumentRequest) => Promise<ProjectContextDocument>;
  update: (docUuid: string, data: UpdateContextDocumentRequest) => Promise<ProjectContextDocument>;
  remove: (docUuid: string) => Promise<void>;
}

export function useContextDocs(
  projectUuid: string,
  sourceSystem?: string | null
): UseContextDocsReturn {
  const [documents, setDocuments] = React.useState<ProjectContextDocument[]>([]);
  const [status, setStatus] = React.useState<ContextDocsStatus>('idle');
  const [error, setError] = React.useState<string | null>(null);

  const refresh = React.useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const { documents: docs } = await contextDocsApi.list(projectUuid, sourceSystem);
      setDocuments(docs);
      setStatus('idle');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
      setStatus('error');
    }
  }, [projectUuid, sourceSystem]);

  // Load on mount and when projectUuid/sourceSystem changes
  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = React.useCallback(
    async (data: CreateContextDocumentRequest): Promise<ProjectContextDocument> => {
      const { document } = await contextDocsApi.create(projectUuid, {
        ...data,
        sourceSystem: data.sourceSystem ?? sourceSystem ?? null,
      });
      setDocuments((prev) => [document, ...prev]);
      return document;
    },
    [projectUuid, sourceSystem]
  );

  const update = React.useCallback(
    async (
      docUuid: string,
      data: UpdateContextDocumentRequest
    ): Promise<ProjectContextDocument> => {
      const { document } = await contextDocsApi.update(projectUuid, docUuid, data);
      setDocuments((prev) => prev.map((d) => (d.uuid === docUuid ? document : d)));
      return document;
    },
    [projectUuid]
  );

  const remove = React.useCallback(
    async (docUuid: string): Promise<void> => {
      await contextDocsApi.delete(projectUuid, docUuid);
      setDocuments((prev) => prev.filter((d) => d.uuid !== docUuid));
    },
    [projectUuid]
  );

  return { documents, status, error, refresh, create, update, remove };
}
